import { useMemo, useState } from "react";
import type { MouseEvent } from "react";
import Button from "components/Button";
import ProductCard from "components/ProductCard";
import { useArrangement } from "./ArrangementContext";
import UrnDetailModal from "./UrnDetailModal";
import { canEngraveUrn } from "./engravingEligibility";
import type { UrnProduct } from "./types";

export default function IncludedUrnStep() {
  const {
    state,
    setIncludedUrn,
    setIncludedUrnEngraving,
    packageInclusions,
    config,
    isReadOnly,
  } = useArrangement();

  const [modalUrn, setModalUrn] = useState<UrnProduct | null>(null);

  // Only the selected urn's variant and engraving belong to it. Details open on
  // unselected urns too, and UrnDetailModal accepts currentVariantId without
  // checking that it belongs to the urn being shown, so handing them over would
  // let "Choose this urn" save urn B against urn A's variant — a pair the
  // selections API rejects as invalid. Passing nothing instead makes the modal
  // initialize from the urn it's actually showing.
  const isModalUrnSelected = !!modalUrn && state.includedUrnId === modalUrn.id;

  const eligibleUrns = useMemo(() => {
    const urns = config?.urns ?? [];
    const eligibleIds = packageInclusions?.eligibleUrnIds ?? [];
    return urns.filter((u) => eligibleIds.includes(u.id));
  }, [config?.urns, packageInclusions?.eligibleUrnIds]);

  // Dropping an ineligible urn and selecting the lone eligible one both live in
  // ArrangementContext, so they hold on every route rather than only while this
  // section is mounted.

  return (
    <div>
      <div className="grid grid-cols-1 justify-items-center gap-20 sm:grid-cols-2 md:grid-cols-3">
        {eligibleUrns.map((urn) => {
          const isSelected = state.includedUrnId === urn.id;
          const selectedVariant = isSelected
            ? urn.variants.find((v) => v.id === state.includedUrnVariantId)
            : null;

          // Stops the card's click-to-select so opening details is its own
          // action; the modal's own "Choose this urn" CTA does the selecting.
          const openDetails = (e: MouseEvent) => {
            e.stopPropagation();
            setModalUrn(urn);
          };

          return (
            <div
              key={urn.id}
              className={isReadOnly || isSelected ? "" : "cursor-pointer"}
              onClick={() => {
                if (!isReadOnly && !isSelected) {
                  setIncludedUrn(urn.id, urn.variants[0]?.id || "");
                }
              }}
            >
              <ProductCard
                name={urn.name}
                price={urn.price}
                image={selectedVariant?.image || urn.image}
                selected={isSelected}
                includedInPlan={isSelected}
                hidePrice={!isSelected}
              >
                {/*
                  No Remove action here: the urn is part of the package, so
                  declining it isn't a choice the family has on this screen.
                  Unselected urns keep a details button (oat, same as the
                  additional-urns grid) so a family can compare the options
                  before choosing rather than having to select one to look.
                */}
                {isSelected ? (
                  <Button onClick={openDetails} variant="secondary" fullWidth>
                    {canEngraveUrn(urn) ? "Personalize" : "View details"}
                  </Button>
                ) : (
                  !isReadOnly && (
                    <Button onClick={openDetails} variant="tertiary" fullWidth>
                      View details
                    </Button>
                  )
                )}
              </ProductCard>
            </div>
          );
        })}
      </div>

      <UrnDetailModal
        urn={modalUrn}
        isOpen={!!modalUrn}
        onClose={() => setModalUrn(null)}
        onSelect={(urnId, variantId, _quantity, engraving) => {
          setIncludedUrn(urnId, variantId);
          setIncludedUrnEngraving(engraving ?? null);
        }}
        readOnly={isReadOnly}
        currentVariantId={
          isModalUrnSelected ? state.includedUrnVariantId : null
        }
        currentQuantity={1}
        currentEngraving={
          isModalUrnSelected ? state.includedUrnEngraving : null
        }
        hideQuantity
      />
    </div>
  );
}
